import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";

import { useAppDispatch } from "app/hooks";
import {
  setAdditionalAction,
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import useTextsNotDeliveredEnabled from "features/inbox/texts-not-delivered/use-texts-not-delivered-enabled.hook";
import useTextRoutingEnabled from "hooks/use-text-routing-enabled.hook";
import { smsDeliveryFailuresApi } from "services/sms-delivery-failures";
import { textRoutingApi } from "services/text-routing";

import { useBrowserNotifications } from "./browser-notifications.context";
import { buildTextAlertContent } from "./text-routing-alerts.util";

// Same wire string as `NewUserNotificationType` in hooks/use-notifications.tsx,
// restated so this module does not pull the whole socket hook in.
const NEW_USER_NOTIFICATION = "new.user.notification";
const SEEN_IDS_LIMIT = 200;

function OpenConversationAction({ patientId }: { patientId: number }) {
  const navigate = useNavigate();
  return (
    <Button
      color="inherit"
      size="small"
      onClick={() => navigate(`/inbox/${patientId}`)}
    >
      Open
    </Button>
  );
}

/**
 * Patient text routing: alerts for a newly routed patient text.
 *
 * - Desktop notification (preference "New patient texts") while Suno is in the
 *   background — the browser-notifications dispatcher enforces backgrounded,
 *   permission, preference and cross-tab de-duplication. One banner per
 *   patient (`collapseKey`); clicking it opens `/inbox/<patient>`.
 * - In-app toast only while this tab is focused and the user is NOT already
 *   looking at that conversation.
 * - Refreshes the waiting counts (Inbox tabs + side-menu badge).
 *
 * Call it exactly once, from the always-mounted bell (`NotificationsMenu`), and
 * hand it that hook instance's `lastJsonMessage`: `useNotifications()` is used
 * by several components, and running this inside it would toast once per
 * instance. Does nothing unless routing is on.
 *
 * Texts not delivered (`extra.kind === "sms_not_delivered"`): the same two
 * alerts for a staff-typed text that failed, gated on TEXTS_NOT_DELIVERED_FEATURE
 * alone (routing need not be on). Toast: `Text to <patient> not delivered · <reason>`
 * with Open; the desktop banner follows the same "New patient texts" preference.
 * With the flag off such a row is only marked seen: it never raises the routing
 * alert, and switching the flag on never replays it.
 */
export default function useTextRoutingAlerts(lastJsonMessage: unknown) {
  const { routingOn } = useTextRoutingEnabled();
  const isTextsNotDeliveredOn = useTextsNotDeliveredEnabled();
  const { notify } = useBrowserNotifications();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const seenIds = useRef<number[]>([]);

  useEffect(() => {
    const socketMessage = lastJsonMessage as {
      type?: string;
      message?: unknown;
    } | null;
    if (socketMessage?.type !== NEW_USER_NOTIFICATION) return;
    const alert = buildTextAlertContent(socketMessage.message);
    if (!alert) return;
    // `lastJsonMessage` is re-read on unrelated re-renders; act once per row.
    if (seenIds.current.includes(alert.notificationId)) return;
    seenIds.current = [...seenIds.current, alert.notificationId].slice(
      -SEEN_IDS_LIMIT
    );
    // Marked seen even while routing is off, so switching routing on does not
    // replay the notification that is still sitting in `lastJsonMessage`.
    const { patientId, notDelivered } = alert;
    if (notDelivered ? !isTextsNotDeliveredOn : !routingOn) return;

    const conversationPath = patientId ? `/inbox/${patientId}` : "/inbox";

    // Looking at it = the Inbox thread or the chart's Messages tab for them.
    const isLookingAtIt =
      patientId !== null &&
      (pathnameRef.current === conversationPath ||
        pathnameRef.current.startsWith(`/patients/${patientId}/messages`));
    const isFocused = !document.hidden && document.hasFocus();

    if (notDelivered) {
      // The thread alert, the header chip and the tab read the patient record and
      // the tab's own reads, and nothing else refetches them when the carrier's
      // answer lands seconds after the send: refresh them now, so a sender still
      // looking at the thread sees the alert without reloading.
      if (patientId !== null) {
        dispatch(
          smsDeliveryFailuresApi.util.invalidateTags([
            { type: "Patient", id: patientId },
            "SmsDeliveryFailures"
          ])
        );
      }
      // The failed text also shows on the chart's Touchpoints → Messages tab.
      const isLookingAtFailure =
        isLookingAtIt ||
        (patientId !== null &&
          pathnameRef.current.startsWith(
            `/patients/${patientId}/touchpoints/messages`
          ));
      notify({
        type: "texts",
        id: String(alert.notificationId),
        title: alert.title,
        body: alert.body,
        collapseKey: patientId ? `sms-not-delivered:${patientId}` : undefined,
        onClick: () => navigate(conversationPath)
      });
      if (isFocused && !isLookingAtFailure) {
        dispatch(setCustomMessage(notDelivered.toastMessage));
        dispatch(
          setAdditionalAction(
            patientId ? (
              <OpenConversationAction patientId={patientId} />
            ) : undefined
          )
        );
        dispatch(setOpen(true));
      }
      return;
    }

    dispatch(textRoutingApi.util.invalidateTags(["TextRoutingCounts"]));

    notify({
      type: "texts",
      id: String(alert.notificationId),
      title: alert.title,
      body: alert.body,
      collapseKey: patientId ? `patient:${patientId}` : undefined,
      onClick: () => navigate(conversationPath)
    });

    if (isFocused && !isLookingAtIt) {
      dispatch(
        // The title already reads "New Message from <patient>", so a notification
        // without a why line shows the title alone (never "New text from New Message…").
        setCustomMessage(
          alert.reasonLine
            ? `${alert.title} · ${alert.reasonLine}`
            : alert.title
        )
      );
      dispatch(
        setAdditionalAction(
          patientId ? (
            <OpenConversationAction patientId={patientId} />
          ) : undefined
        )
      );
      dispatch(setOpen(true));
    }
  }, [
    lastJsonMessage,
    routingOn,
    isTextsNotDeliveredOn,
    notify,
    dispatch,
    navigate
  ]);
}
